class BXVariantSubscriptionSelector extends HTMLElement {
  constructor() {
    super();
    this.activeVariant = null;
    this.allVariants = [];
    this.allSellingPlanGroups = [];
    this.variantItems = [];
    this.purchaseType = 'onetime'; // 'onetime' or 'subscription'
    this.selectedSellingPlanId = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Get data from window
    this.activeVariant = window.bxActiveVariant || null;
    this.allVariants = window.bxAllVariants || [];
    this.allSellingPlanGroups = window.bxAllSellingPlanGroups || [];
    
    // Get variant items
    this.variantItems = this.querySelectorAll('[data-variant-item]');
    
    // Get purchase option items
    this.purchaseOptionItems = this.querySelectorAll('[data-option]');
    
    // Get add to cart button
    this.addToCartButton = this.querySelector('#add-to-cart-btn');
    
    // Set initial active variant
    if (this.activeVariant) {
      this.setActiveVariant(this.activeVariant);
    }
    
    // Add click listeners to variant items
    this.variantItems.forEach(item => {
      item.addEventListener('click', () => {
        const variantId = parseInt(item.getAttribute('data-variant-id'));
        const variant = this.allVariants.find(v => v.id === variantId);
        if (variant) {
          this.setActiveVariant(variant);
        }
      });
    });
    
    // Add click listeners to purchase option items (one-time vs subscription)
    this.purchaseOptionItems.forEach(item => {
      item.addEventListener('click', () => {
        const option = item.getAttribute('data-option');
        this.setPurchaseType(option);
      });
    });
    
    // Add click listener to add to cart button
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', () => {
        this.handleAddToCart();
      });
    }
    
    // Initial render of selling plan groups
    this.renderSellingPlanGroups();
    
    // Set initial purchase type based on active option
    const activeOption = this.querySelector('[data-option].active');
    if (activeOption) {
      this.setPurchaseType(activeOption.getAttribute('data-option'));
    }
  }

  setActiveVariant(variant) {
    this.activeVariant = variant;
    
    // Reset selected selling plan when variant changes
    this.selectedSellingPlanId = null;
    
    // Update active state on variant items
    this.variantItems.forEach(item => {
      const variantId = parseInt(item.getAttribute('data-variant-id'));
      if (variantId === variant.id) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });
    
    // Update window.bxActiveVariant
    window.bxActiveVariant = variant;
    
    // Render selling plan groups for new variant
    this.renderSellingPlanGroups();
    
    // Update prices and other variant-dependent content
    this.updateVariantContent(variant);
    
    // Auto-select first selling plan if subscription is active
    if (this.purchaseType === 'subscription' && variant.selling_plan_allocations?.length > 0) {
      const firstAllocation = variant.selling_plan_allocations[0];
      const firstPlan = this.allSellingPlanGroups
        .flatMap(group => group.selling_plans || [])
        .find(plan => plan.id === firstAllocation.selling_plan_id);
      
      if (firstPlan) {
        const matchedGroup = this.allSellingPlanGroups.find(
          group => group.selling_plans?.some(p => p.id === firstPlan.id)
        );
        this.selectSellingPlan(firstPlan, matchedGroup, firstAllocation);
      }
    }
    
    // Update current subscription frequency if subscription is active and plan is selected
    if (this.purchaseType === 'subscription' && this.selectedSellingPlanId) {
      const selectedPlan = this.allSellingPlanGroups
        .flatMap(group => group.selling_plans || [])
        .find(plan => plan.id === this.selectedSellingPlanId);
      
      if (selectedPlan) {
        const currentFrequencyElement = this.querySelector('[current-subscription-frequency]');
        if (currentFrequencyElement && selectedPlan.name) {
          currentFrequencyElement.textContent = selectedPlan.name;
        }
      }
    }
  }

  updateVariantContent(variant) {
    // Update one-time price
    const onetimePriceElement = this.querySelector('[data-price="onetime"]');
    if (onetimePriceElement && variant.price) {
      onetimePriceElement.textContent = this.formatMoney(variant.price);
    }
    
    // Update subscription price if available
    const subscriptionPriceElements = this.querySelectorAll('[data-price="subscription"]');
    if (subscriptionPriceElements && variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
      subscriptionPriceElements.forEach(element => {
        element.textContent = this.formatMoney(variant.selling_plan_allocations[0].price);
      });
    }
    
    // Update compare at price
    const subscriptionComparePriceElements = this.querySelectorAll('[data-price="subscription-compare"]');
    if (subscriptionComparePriceElements) {
      const comparePrice = variant.compare_at_price || variant.price;
      subscriptionComparePriceElements.forEach(element => {
        element.textContent = this.formatMoney(comparePrice);
      });
    }
    
    // Update included summary
    const activeVariantTitle = this.querySelector('[active-variant-title]');
    if (activeVariantTitle) {
      activeVariantTitle.textContent = variant.title;
    }
    
    // Update all active variant price elements (including button)
    const activeVariantPriceElements = this.querySelectorAll('[active-variant-price]');
    if (activeVariantPriceElements) {
      activeVariantPriceElements.forEach(element => {
        element.textContent = this.formatMoney(variant.price);
      });
    }
    
    const activeVariantCompareAtPrice = this.querySelector('[active-variant-compare-at-price]');
    if (activeVariantCompareAtPrice) {
      activeVariantCompareAtPrice.textContent = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : '';
    }
  }

  renderSellingPlanGroups() {
    if (!this.activeVariant || !this.activeVariant.selling_plan_allocations) {
      return;
    }

    const frequencyOptionsContainer = this.querySelector('#susbcription-frequency-options');
    if (!frequencyOptionsContainer) {
      return;
    }

    // Get selling_plan_group_ids from variant's selling_plan_allocations
    const sellingPlanGroupIds = this.activeVariant.selling_plan_allocations.map(
      allocation => allocation.selling_plan_group_id
    );

    // Filter selling plan groups that match
    const matchedSellingPlanGroups = this.allSellingPlanGroups.filter(
      group => sellingPlanGroupIds.includes(group.id)
    );

    // Clear existing content
    frequencyOptionsContainer.innerHTML = '';

    // Render matched selling plan groups
    matchedSellingPlanGroups.forEach(group => {
      if (group.selling_plans && group.selling_plans.length > 0) {
        group.selling_plans.forEach(plan => {
          // Find the corresponding allocation for this plan
          const allocation = this.activeVariant.selling_plan_allocations.find(
            a => a.selling_plan_id === plan.id
          );
          
          // Only render plans that have allocations for this variant
          if (!allocation) {
            return;
          }
          
          const optionElement = document.createElement('button');
          optionElement.className = 'susbcription-frequency-option';
          
          optionElement.textContent = plan.name || plan.options?.value || 'Plan';

          optionElement.setAttribute('data-selling-plan-id', plan.id);
          optionElement.setAttribute('data-selling-plan-group-id', group.id);
          optionElement.setAttribute('data-allocation-price', allocation.price);
          optionElement.setAttribute('data-plan-name', plan.name);
          // Add click handler
          optionElement.addEventListener('click', () => {
            this.selectSellingPlan(plan, group, allocation);
          });
          
          frequencyOptionsContainer.appendChild(optionElement);
        });
      }
    });
    
    // Auto-select first plan if subscription is active and no plan is selected yet
    if (this.purchaseType === 'subscription' && !this.selectedSellingPlanId && matchedSellingPlanGroups.length > 0) {
      const firstGroup = matchedSellingPlanGroups[0];
      if (firstGroup.selling_plans && firstGroup.selling_plans.length > 0) {
        const firstPlan = firstGroup.selling_plans[0];
        const firstAllocation = this.activeVariant.selling_plan_allocations.find(
          a => a.selling_plan_id === firstPlan.id
        );
        
        if (firstAllocation) {
          // Auto-select without triggering full selectSellingPlan (to avoid duplicate updates)
          this.selectedSellingPlanId = firstPlan.id;
          const firstOptionElement = frequencyOptionsContainer.querySelector(`[data-selling-plan-id="${firstPlan.id}"]`);
          if (firstOptionElement) {
            firstOptionElement.classList.add('active');
          }
          
          // Update delivery date
          this.updateNextDeliveryDate(firstPlan.name);
          
          // Update subscription price
          const subscriptionPriceElement = this.querySelector('[data-price="subscription"]');
          if (subscriptionPriceElement && firstAllocation) {
            subscriptionPriceElement.textContent = this.formatMoney(firstAllocation.price);
          }
          
          // Update current subscription frequency display
          const currentFrequencyElement = this.querySelector('[current-subscription-frequency]');
          if (currentFrequencyElement && firstPlan.name) {
            currentFrequencyElement.textContent = firstPlan.name;
          }
        }
      }
    }
  }

  selectSellingPlan(plan, group, allocation) {
    // Store selected selling plan ID
    this.selectedSellingPlanId = plan.id;
    
    // Update active state on frequency options
    const frequencyOptions = this.querySelectorAll('.susbcription-frequency-option');
    frequencyOptions.forEach(option => {
      option.classList.remove('active');
      if (parseInt(option.getAttribute('data-selling-plan-id')) === plan.id) {
        option.classList.add('active');
      }
    });
    
    // Update subscription price
    const subscriptionPriceElement = this.querySelector('[data-price="subscription"]');
    if (subscriptionPriceElement && allocation) {
      subscriptionPriceElement.textContent = this.formatMoney(allocation.price);
    }
    
    // Update next delivery date based on plan name
    this.updateNextDeliveryDate(plan.name);
    
    // Update current subscription frequency display
    const currentFrequencyElement = this.querySelector('[current-subscription-frequency]');
    if (currentFrequencyElement && plan.name) {
      currentFrequencyElement.textContent = plan.name;
    }
    
    // Update recurring summary if needed
    // This can be extended based on your requirements
  }

  updateNextDeliveryDate(planName) {
    const deliveryDateElement = this.querySelector('[next-delivery-date]');
    if (!deliveryDateElement || !planName) {
      return;
    }

    // Extract number of months from plan name (e.g., "Every 3 months" -> 3)
    const monthsMatch = planName.match(/(\d+)\s*month/i);
    if (!monthsMatch) {
      return;
    }

    const monthsToAdd = parseInt(monthsMatch[1]);
    
    // Calculate next delivery date from today
    const today = new Date();
    const nextDate = new Date(today);
    nextDate.setMonth(today.getMonth() + monthsToAdd);
    
    // Format date with ordinal suffix (e.g., "15th Apr 2026")
    const formattedDate = this.formatDateWithOrdinal(nextDate);
    deliveryDateElement.textContent = formattedDate;
  }

  formatDateWithOrdinal(date) {
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    
    // Get ordinal suffix (st, nd, rd, th)
    const getOrdinalSuffix = (d) => {
      if (d > 3 && d < 21) return 'th';
      switch (d % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  }

  setPurchaseType(type) {
    this.purchaseType = type;
    
    // Update active state on purchase option items
    this.purchaseOptionItems.forEach(item => {
      if (item.getAttribute('data-option') === type) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });
    
    // Update main element classes
    if (type === 'subscription') {
      this.classList.remove('subscriptionInactive');
      this.classList.add('subscriptionActive');
      
      // Auto-select first selling plan if available and none selected
      if (!this.selectedSellingPlanId && this.activeVariant?.selling_plan_allocations?.length > 0) {
        const firstAllocation = this.activeVariant.selling_plan_allocations[0];
        const firstPlan = this.allSellingPlanGroups
          .flatMap(group => group.selling_plans || [])
          .find(plan => plan.id === firstAllocation.selling_plan_id);
        
        if (firstPlan) {
          const matchedGroup = this.allSellingPlanGroups.find(
            group => group.selling_plans?.some(p => p.id === firstPlan.id)
          );
          this.selectSellingPlan(firstPlan, matchedGroup, firstAllocation);
        }
      }
    } else {
      this.classList.remove('subscriptionActive');
      this.classList.add('subscriptionInactive');
      this.selectedSellingPlanId = null;
      
      // Clear delivery date when switching to one-time
      const deliveryDateElement = this.querySelector('[next-delivery-date]');
      if (deliveryDateElement) {
        deliveryDateElement.textContent = '';
      }
      
      // Clear current subscription frequency when switching to one-time
      const currentFrequencyElement = this.querySelector('[current-subscription-frequency]');
      if (currentFrequencyElement) {
        currentFrequencyElement.textContent = '';
      }
    }
  }

  buildCartItems() {
    if (!this.activeVariant) {
      return null;
    }

    const item = {
      id: this.activeVariant.id,
      quantity: 1,
      properties: {}
    };

    if (this.purchaseType === 'subscription' && this.selectedSellingPlanId) {
      item.selling_plan = this.selectedSellingPlanId;
    }

    return {
      items: [item]
    };
  }

  async refreshCartSections() {
    try {
      // Fetch updated cart sections
      const sections = 'cart-drawer,cart-bubble';
      const response = await fetch(
        `${window.location.pathname}?sections=${sections}`
      );

      if (!response.ok) {
        throw new Error('Failed to refresh cart sections');
      }

      const parsedState = await response.json();

      // Update cart drawer content
      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer && parsedState['cart-drawer']) {
        const cartDrawerContent =
          cartDrawer.querySelector('.cart-drawer') || cartDrawer;
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(
          parsedState['cart-drawer'],
          'text/html'
        );
        const newContent = htmlDoc.querySelector('.cart-drawer');

        if (newContent) {
          cartDrawerContent.innerHTML = newContent.innerHTML;

          // Re-initialize cart drawer functionality
          if (cartDrawer.notesToggle) {
            cartDrawer.notesToggle();
          }
          if (cartDrawer.removeProductEvent) {
            cartDrawer.removeProductEvent();
          }
        }
      }

      // Update cart bubble (item count)
      const cartBubble = document.getElementById('cart-drawer-toggle');
      if (cartBubble && parsedState['cart-bubble']) {
        const cartCount = cartBubble.querySelector('.thb-item-count');
        if (cartCount) {
          const parser = new DOMParser();
          const htmlDoc = parser.parseFromString(
            parsedState['cart-bubble'],
            'text/html'
          );
          const newCount = htmlDoc.querySelector('.thb-item-count');
          if (newCount) {
            cartCount.innerHTML = newCount.innerHTML;
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing cart sections:', error);
      // Fallback to theme's refresh method
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
        })
      );
    }
  }

  openCartDrawer() {
    // Use the existing cart drawer toggle mechanism
    const cartToggleButton = document.getElementById('cart-drawer-toggle');
    if (cartToggleButton) {
      // Simulate the cart toggle button click to ensure proper opening
      cartToggleButton.click();
    } else {
      // Fallback: manually open cart drawer using the same logic as the theme
      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        document.body.classList.add('open-cc');
        document.body.classList.add('open-cart');
        cartDrawer.classList.add('active');
        cartDrawer.focus();

        // Trigger cart drawer open event (theme's custom event function)
        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart-drawer:open');
        } else {
          document.dispatchEvent(
            new CustomEvent('cart-drawer:open', {
              bubbles: true,
            })
          );
        }

        // Activate recommendations if available
        setTimeout(() => {
          const recommendations = cartDrawer.querySelector(
            '.product-recommendations--full'
          );
          if (recommendations) {
            recommendations.classList.add('active');
          }
        }, 100);
      }
    }
  }

  async handleAddToCart() {
    // Prevent multiple clicks
    if (!this.activeVariant) {
      console.error('No active variant selected');
      return;
    }

    if (this.purchaseType === 'subscription' && !this.selectedSellingPlanId) {
      console.error('Subscription selected but no selling plan chosen');
      return;
    }

    if (this.addToCartButton && this.addToCartButton.disabled) {
      return;
    }

    const cartData = this.buildCartItems();
    if (!cartData) {
      return;
    }

    // Disable button and show loading state
    if (this.addToCartButton) {
      this.addToCartButton.disabled = true;
      const buttonText = this.addToCartButton.querySelector('.add-to-cart-btn-text');
      if (buttonText) {
        buttonText.textContent = 'Adding...';
      }
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add item to cart');
      }

      const data = await response.json();

      // Wait for cart refresh to complete before opening drawer
      await this.refreshCartSections();

      // Trigger item added event
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:item-added', {
          bubbles: true,
          detail: {
            product: data.hasOwnProperty('items') ? data.items[0] : data,
          },
        })
      );

      // Open cart drawer after cart content is refreshed
      this.openCartDrawer();

      // Trigger cart drawer update
      document.dispatchEvent(new CustomEvent('cart:update'));

      // Update button state
      if (this.addToCartButton) {
        const buttonText = this.addToCartButton.querySelector('.add-to-cart-btn-text');
        if (buttonText) {
          buttonText.textContent = 'Added to Cart ✓';
        }
        
        // Reset button after 2 seconds
        setTimeout(() => {
          this.addToCartButton.disabled = false;
          if (buttonText) {
            buttonText.textContent = 'ADD TO CART';
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Re-enable button and remove loading state
      if (this.addToCartButton) {
        this.addToCartButton.disabled = false;
        const buttonText = this.addToCartButton.querySelector('.add-to-cart-btn-text');
        if (buttonText) {
          buttonText.textContent = 'ADD TO CART';
        }
      }
    }
  }

  formatMoney(cents) {
    if (typeof cents !== 'number') {
      return '';
    }
    
    // Use Shopify's formatMoney if available
    if (typeof formatMoney === 'function' && window.theme?.settings?.money_with_currency_format) {
      return formatMoney(cents, window.theme.settings.money_with_currency_format);
    }
    
    // Fallback: format like money_without_trailing_zeros
    const dollars = (cents / 100).toFixed(0);
    const formatted = dollars.replace(/\.00$/, '');
    
    // Get currency symbol from Shopify if available
    const currencySymbol = window.Shopify?.currency?.active || '$';
    return `${currencySymbol}${formatted}`;
  }
}

if (!customElements.get('bx-variant-subscription-selector')) {
  customElements.define('bx-variant-subscription-selector', BXVariantSubscriptionSelector);
}
